"use client";
import React, { useMemo, useState } from "react";
import MarkdownEditorDialog from "../components/MarkdownEditorDialog";
import AiGeneratedReviewDialog from "@/components/AiGeneratedReviewDialog";
import useProjectAiReview from "@/hooks/useProjectAiReview";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
  DialogTrigger,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Input } from "../../@/components/ui/input";
import { Textarea } from "../../@/components/ui/textarea";
import {
  Eye,
  Clock,
  Link2,
  ExternalLink,
  File,
  BookOpen,
  User,
} from "lucide-react";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import axios from "axios";
import { toast } from "sonner";
import { ClipLoader } from "react-spinners";
import { formatUtcToUserTimezone } from "../lib/timezone";

const feedbackTemplates = [
  "Excellent Work",
  "Good Effort",
  "Great UI/UX Design",
  "Needs Revision",
];

export default function ReviewProjectDialog({
  submission = null,
  gradedBy,
  onReviewSubmitted,
  courseName,
  topicTitle,
  userFirstName,
  userLastName,
  userEmail,
  triggerLabel = "Review",
  triggerVariant = "outline",
  triggerClassName = "",
  showTriggerIcon = true,
}) {
  const submissionData = useMemo(() => {
    if (!submission) {
      return {
        id: null,
        submissionId: null,
        student_id: null,
        project_id: null,
        studentName:
          userFirstName && userLastName
            ? `${userFirstName} ${userLastName}`
            : "Not available",
        studentEmail: userEmail ?? "Not available",
        submissionDate: null,
        submissionUrl: null,
        liveUrl: null,
        file_url: null,
        course: courseName ?? "Not available",
        topicTitle: topicTitle ?? null,
        projectTitle: "Not available",
        projectDescription: "Not available",
        passmark: 50,
        maxPoints: 100,
      };
    }

    return {
      id: null,
      submissionId: null,
      student_id:
        submission.student_id ??
        submission.studentId ??
        submission.user_id ??
        submission.student?.id ??
        null,
      project_id:
        submission.project_id ??
        submission.projectId ??
        submission.project?.id ??
        null,
      studentName:
        submission.student_name ??
        submission.studentName ??
        (userFirstName && userLastName
          ? `${userFirstName} ${userLastName}`
          : "Not available"),
      studentEmail:
        submission.student_email ??
        submission.studentEmail ??
        userEmail ??
        "Not available",
      submissionDate: submission.submitted_on ?? null,
      submissionUrl:
        submission.submission_url ??
        submission.github_repo ??
        submission.githubUrl ??
        null,
      liveUrl: submission.live_url ?? submission.liveUrl ?? null,
      file_url: submission.file_url ?? submission.file ?? null,
      course:
        submission.project?.course ??
        submission.project?.topic_name ??
        courseName ??
        "Not available",
      topicTitle:
        submission.project?.topic_name ??
        submission.project?.topic?.title ??
        topicTitle ??
        null,
      projectTitle: submission.project?.title ?? "Not available",
      projectDescription: submission.project?.description ?? "Not available",
      passmark: submission.project?.passmark ?? 50,
      maxPoints: submission.project?.score ?? 100,
    };
  }, [submission, courseName, topicTitle, userEmail, userFirstName, userLastName]);

  const [open, setOpen] = useState(false);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState(feedbackTemplates[0]);
  const [selectedTemplate, setSelectedTemplate] = useState(
    feedbackTemplates[0]
  );
  const [loading, setLoading] = useState(false);
  const [markdownEditorOpen, setMarkdownEditorOpen] = useState(false);
  const [aiReviewOpen, setAiReviewOpen] = useState(false);
  const { getToken, userId } = useAuth(); // getToken for auth header, userId as fallback

  const {
    aiReviewText,
    hasAIReview,
    latestAIReview,
    loading: projectReviewsLoading,
    deleting: aiReviewDeleting,
    rejectLatest: rejectLatestAIReview,
  } = useProjectAiReview({
    projectId: submissionData.project_id,
    studentId: submissionData.student_id,
    enabled: open,
  });

  const actualPassmark = useMemo(() => {
    return Math.round(
      ((submissionData.passmark ?? 50) / 100) *
        (submissionData.maxPoints ?? 100)
    );
  }, [submissionData.passmark, submissionData.maxPoints]);

  // Keep score in sync when a different submission is passed in
  React.useEffect(() => {
    setScore(0);
    setSelectedTemplate(feedbackTemplates[0]);
    setFeedback(feedbackTemplates[0]);
  }, [submissionData]);

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setFeedback(template);
  };

  const handleOpenMarkdownEditor = (e) => {
    e?.preventDefault?.();
    setMarkdownEditorOpen(true);
  };

  const handleMarkdownSave = (newContent) => {
    setFeedback(newContent);
    setMarkdownEditorOpen(false);
  };

  // AI reviews are fetched by useProjectAiReview when dialog opens.

  const handleSubmit = async () => {
    if (!feedback.trim()) {
      console.log("Feedback is required");
      return;
    }

    const grader = gradedBy ?? userId;
    if (!grader) {
      console.log("Graded by user ID is required");
      return;
    }

    // derive student and project ids with safe fallbacks
    const studentId =
      submissionData.student_id ??
      submission?.user_id ??
      submission?.user?.id ??
      submission?.student?.user_id ??
      submission?.student?.id ??
      submission?.student?._id ??
      userId ??
      null;

    const projectId =
      submissionData.project_id ??
      submission?.project_id ??
      submission?.projectId ??
      submission?.project?.id ??
      submission?.project?._id ??
      null;

    if (!studentId || !projectId) {
      console.log(
        "Student ID and Project ID are required - submission data may be missing"
      );
      return;
    }

    setLoading(true);

    const reviewData = {
      student_id: String(studentId),
      project_id: String(projectId),
      reviewed_by: String(grader),
      feedback: feedback,
      score: parseFloat(score),
      is_passed: score >= (actualPassmark || 50),
    };

    console.log("Review submission payload:", reviewData);

    try {
      // get Clerk token and send as Bearer
      const token = await getToken();
      if (!token) return;

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const reviewResponse = await axios.post(
        api_endpoint + "/project/review",
        reviewData,
        reqConfig
      );

      if (reviewResponse.status >= 200 && reviewResponse.status < 300) {
        console.log("Feedback submitted successfully!", reviewResponse.data);
        setOpen(false);
        setScore(submissionData.passmark || 50);
        setFeedback("");
        setSelectedTemplate(feedbackTemplates[0]);
        if (onReviewSubmitted) onReviewSubmitted();
      } else {
        console.error(
          "Failed to submit feedback",
          reviewResponse.status,
          reviewResponse.data
        );
        toast.error("Failed to submit review");
      }
    } catch (error) {
      console.error(
        "Error submitting feedback:",
        error?.response?.data ?? error
      );
      toast.error("Failed to submit review");
    } finally {
      setLoading(false);
    }
  };

  // safe denominator for gradient (avoid division by zero)
  const denom = submissionData.maxPoints > 0 ? submissionData.maxPoints : 100;
  const fillPercent = Math.min(100, Math.max(0, (score / denom) * 100));

  const handleUseAiReview = () => {
    if (!hasAIReview) return;
    setSelectedTemplate("");
    setFeedback(aiReviewText);
    setAiReviewOpen(false);
  };

  const handleRejectAiReview = async () => {
    const ok = await rejectLatestAIReview();
    if (ok) setAiReviewOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={triggerVariant} className={triggerClassName}>
          {showTriggerIcon ? <Eye className="h-4 w-4" /> : null}
          {triggerLabel}
        </Button>
      </DialogTrigger>

      <DialogContent className="">
        <DialogHeader className="">
          <DialogTitle className="font-heading text-xl text-(--neutral-900)">
            Review Submission
          </DialogTitle>
          <div className="text-sm text-(--neutral-600)">
            {submissionData.projectTitle !== "Not available" && (
              <div className="font-medium font-body text-(--neutral-800) mb-1">
                {submissionData.projectTitle}
              </div>
            )}
            <div className="font-body">
              View submission details and review the project
            </div>
          </div>
        </DialogHeader>

        <div className="">
          {/* Submission Details */}
          <div className="mb-3">
            <h3 className="text-xl font-bold text-(--neutral-900) font-heading">
              Submission Details
            </h3>

            <div className="">
              <div className="flex items-start gap-3 px-4 py-3">
                <Clock className="w-5 h-5 text-(--neutral-500) mt-0.5" />
                <div className="text-sm">
                  <span className="font-medium text-(--neutral-700) font-heading">
                    Submitted On:
                  </span>
                  <span className="ml-1 text-(--neutral-900) font-body">
                    {submissionData.submissionDate
                      ? formatUtcToUserTimezone(submissionData.submissionDate, null, {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                        })
                      : "Not available"}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3 px-4 py-3">
                <User className="w-5 h-5 text-(--neutral-500) mt-0.5" />
                <div className="text-sm">
                  <span className="font-medium text-(--neutral-700) font-heading">
                    Submitted By:
                  </span>
                  <span className="ml-1 text-(--neutral-900) font-body">
                    {submissionData.studentName || "Not available"}
                    {submissionData.studentEmail &&
                    submissionData.studentEmail !== "Not available"
                      ? ` · ${submissionData.studentEmail}`
                      : ""}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3 px-4 py-3">
                <BookOpen className="w-5 h-5 text-(--neutral-500) mt-0.5" />
                <div className="text-sm">
                  <span className="font-medium text-(--neutral-700) font-heading">
                    Associated Course:
                  </span>
                  <span className="ml-1 text-(--neutral-900) font-body">
                    {submissionData.course || "Not available"}
                    {submissionData.topicTitle
                      ? ` · ${submissionData.topicTitle}`
                      : ""}
                  </span>
                </div>
              </div>

              <div className="border-b border-(--neutral-300)"></div>

              <div className="flex items-start gap-3 px-4 py-3">
                <Link2 className="w-5 h-5 text-(--neutral-500) mt-0.5" />
                <div className="text-sm">
                  <span className="font-medium text-(--neutral-700) font-heading">
                    Submission URL:
                  </span>
                  <span className="ml-1">
                    {submissionData.submissionUrl ? (
                      <a
                        href={submissionData.submissionUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 break-all font-body text-(--neutral-700) hover:text-(--color-accent)"
                      >
                        {submissionData.submissionUrl}
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    ) : (
                      <span className="text-(--neutral-400) font-body">
                        Not available
                      </span>
                    )}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3 px-4 py-3">
                <ExternalLink className="w-5 h-5 text-(--neutral-500) mt-0.5" />
                <div className="text-sm">
                  <span className="font-medium text-(--neutral-700) font-heading">
                    Live URL:
                  </span>
                  <span className="ml-1">
                    {submissionData.liveUrl ? (
                      <a
                        href={submissionData.liveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 break-all font-body text-(--neutral-700) hover:text-(--color-accent)"
                      >
                        {submissionData.liveUrl}
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    ) : (
                      <span className="text-(--neutral-400) font-body">
                        Not available
                      </span>
                    )}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3 px-4 py-3">
                <File className="w-5 h-5 text-(--neutral-500) mt-0.5" />
                <div className="text-sm">
                  <span className="font-medium text-(--neutral-700) font-heading">
                    Additional Files:
                  </span>
                  <span className="ml-1">
                    {submissionData.file_url ? (
                      <a
                        href={submissionData.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 break-all font-body text-(--neutral-700) hover:text-(--color-accent)"
                      >
                        {submissionData.file_url}
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    ) : (
                      <span className="text-(--neutral-400) font-body">
                        No additional file submitted
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* AI Review Banner */}
          {hasAIReview ? (
            <div className="mb-6 flex items-center justify-between gap-2 rounded-lg bg-(--neutral-100) px-3 py-2">
              <div className="text-sm text-(--neutral-700) font-body">
                This project has been reviewed by{" "}
                <span className="font-medium font-heading">Rukia AI</span>
              </div>
              <Button
                variant="link"
                className="p-0 h-auto font-body"
                onClick={() => setAiReviewOpen(true)}
                disabled={projectReviewsLoading}
              >
                View/Use AI Review
                <ExternalLink className="w-4 h-4" />
              </Button>
            </div>
          ) : null}

          {/* Instructor Feedback */}
          <div>
            <h3 className="text-xl font-bold mb-4 text-(--neutral-900) font-heading">
              Instructor Feedback
            </h3>

            <div className="mb-6">
              <label className="block font-medium mb-2 text-(--neutral-700) font-heading">
                Score* (Passmark: {actualPassmark})
              </label>
              <div className="flex items-center gap-4">
                <Input
                  type="number"
                  min={0}
                  max={submissionData.maxPoints}
                  value={score}
                  onChange={(e) => {
                    let val = Number(e.target.value);
                    if (isNaN(val)) val = 0;
                    if (val < 0) val = 0;
                    if (val > submissionData.maxPoints)
                      val = submissionData.maxPoints;
                    setScore(val);
                  }}
                  className="w-24 h-12 text-center text-lg font-medium border-(--neutral-300) font-body"
                />
                <div className="flex-1">
                  <input
                    type="range"
                    min={0}
                    max={submissionData.maxPoints}
                    value={score}
                    onChange={(e) => setScore(Number(e.target.value))}
                    className="w-full h-2 rounded-lg bg-(--neutral-200) appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, var(--color-primary) 0%, var(--color-primary) ${fillPercent}%, var(--neutral-200) ${fillPercent}%, var(--neutral-200) 100%)`,
                    }}
                  />
                </div>
                <span className="text-lg font-medium text-(--neutral-900) min-w-15">
                  {score}/{submissionData.maxPoints}
                </span>
              </div>
              <div className="text-xs text-(--neutral-500) mt-1 font-body">
                Pass: {score >= actualPassmark ? "✅" : "❌"} (Need{" "}
                {actualPassmark}+ to pass)
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <label className="block font-medium text-(--neutral-700) font-heading">
                  Detailed Feedback *
                </label>
                {/* <Button
                  onClick={handleOpenMarkdownEditor}
                  size="sm"
                  variant="outline"
                  className="btn-outline-sm hover:bg-blue hover:text-white hover:border-transparent rounded-[4px] font-heading"
                >
                  Edit in Fullscreen
                </Button> */}
                {/* Markdown Editor Modal */}
                <MarkdownEditorDialog
                  open={markdownEditorOpen}
                  initialValue={feedback}
                  onClose={() => setMarkdownEditorOpen(false)}
                  onSave={handleMarkdownSave}
                />
              </div>
              <Textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={8}
                placeholder="Enter your detailed feedback here or use templates below"
                className="border-(--neutral-300) resize-none font-body"
              />
            </div>

            <div className="mb-6">
              <label className="block font-medium mb-3 text-(--neutral-700) font-heading">
                Quick Feedback Templates
              </label>
              <div className="grid grid-cols-2 gap-3">
                {feedbackTemplates.map((template) => (
                  <Button
                    variant="outline"
                    key={template}
                    className={`flex items-center justify-start gap-3 border px-4 py-3 text-left font-body transition-all ${
                      selectedTemplate === template
                        ? "border-(--color-primary)"
                        : "border-(--neutral-300) hover:border-(--color-accent)"
                    }`}
                    onClick={() => handleTemplateSelect(template)}
                  >
                    <div
                      className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                        selectedTemplate === template
                          ? "border-(--color-primary)"
                          : "border-(--neutral-300) hover:border-(--color-accent)"
                      }`}
                    >
                      {selectedTemplate === template && (
                        <div className="h-2.5 w-2.5 rounded-full bg-(--color-primary)" />
                      )}
                    </div>
                    <span className="text-(--neutral-700)">
                      {template}
                    </span>
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="">
          <DialogClose asChild>
            <Button
              type="button"
              variant="outline"
              className=""
              disabled={loading}
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            onClick={handleSubmit}
            variant=""
            className=""
            disabled={loading || !feedback.trim()}
          >
            {loading ? (
              <ClipLoader size={24} color="#fff" />
            ) : (
              "Submit Feedback"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>

      <AiGeneratedReviewDialog
        open={aiReviewOpen}
        onOpenChange={setAiReviewOpen}
        loading={projectReviewsLoading}
        deleting={aiReviewDeleting}
        hasAIReview={hasAIReview}
        aiReviewText={aiReviewText}
        canReject={Boolean(latestAIReview?.id)}
        onReject={handleRejectAiReview}
        onApproveUse={handleUseAiReview}
      />
    </Dialog>
  );
}
